/**
 * Single source of truth for every locale the app supports.
 *
 * To add a new region, add one entry here — `detect-device-locale.js`,
 * `setup-locale.js`, and `store/merchant.js` all derive their data from
 * this object. Future region-aware files (postal-code validators, address
 * format rules, etc.) should consume the same source.
 *
 * Per entry:
 *   - `region`:   ISO 3166 alpha-2 code. Used to map device locale → preferred
 *                 currency for multi-currency merchants. Omit for fallback-only
 *                 locales (e.g. bare 'en').
 *   - `currency`: ISO 4217 code. Same purpose as `region`. Omit if no preference.
 *   - `moment`:   defineLocale data for moment. Omit when the default 'en'
 *                 locale's behavior is correct for this tag (e.g. en-US, 'en').
 *                 Use the canonical values from `node_modules/moment/locale/<tag>.js`
 *                 — do NOT use side-effect imports (`import 'moment/locale/en-gb'`)
 *                 because under Vite they register against a different moment
 *                 instance than the one moment-timezone wraps; the locale silently
 *                 fails to apply. See setup-locale.js for the full rationale.
 */

const SUPPORTED_LOCALES = {
  en: {
    // Bare 'en' — navigator.language without a region tag. Falls through to
    // moment's default 'en' locale (12-hour, US-style) and to the merchant's
    // first-provider currency.
  },

  'en-US': {
    region: 'US',
    currency: 'USD'
    // No `moment` block — moment's default 'en' locale matches en-US.
  },

  'en-GB': {
    region: 'GB',
    currency: 'GBP',
    moment: {
      parentLocale: 'en',
      longDateFormat: {
        LT: 'HH:mm',
        LTS: 'HH:mm:ss',
        L: 'DD/MM/YYYY',
        LL: 'D MMMM YYYY',
        LLL: 'D MMMM YYYY HH:mm',
        LLLL: 'dddd, D MMMM YYYY HH:mm'
      },
      week: { dow: 1, doy: 4 }
    }
  },

  'en-AU': {
    region: 'AU',
    currency: 'AUD',
    moment: {
      parentLocale: 'en',
      longDateFormat: {
        LT: 'h:mm A',
        LTS: 'h:mm:ss A',
        L: 'DD/MM/YYYY',
        LL: 'D MMMM YYYY',
        LLL: 'D MMMM YYYY h:mm A',
        LLLL: 'dddd, D MMMM YYYY h:mm A'
      },
      week: { dow: 1, doy: 4 }
    }
  },

  'en-CA': {
    region: 'CA',
    currency: 'CAD',
    moment: {
      parentLocale: 'en',
      longDateFormat: {
        LT: 'h:mm A',
        LTS: 'h:mm:ss A',
        L: 'YYYY-MM-DD',
        LL: 'MMMM D, YYYY',
        LLL: 'MMMM D, YYYY h:mm A',
        LLLL: 'dddd, MMMM D, YYYY h:mm A'
      },
      week: { dow: 0, doy: 6 }
    }
  }
};



export default SUPPORTED_LOCALES;
